import { create } from "zustand";
import { persist } from "zustand/middleware";
import { atelierApi } from "@services/api/atelier";
import { disponibilitesApi } from "@services/api/disponibilites";
import {
  WorkshopJobStatus,
  WorkshopSpecialty,
  WorkshopStatusConfig,
  WorkshopWorker,
  WorkshopJob,
  WorkCharge,
  CarWindow,
} from "../types/garage/atelier";
import { useDataStore } from "./user_org";
import { usePlanningStore, type CompactionMode } from "./planningStore";
import type { User } from "../types/user";
import type { OrdreStatus } from "../types/ordre";
import { collection, query, where, onSnapshot, Timestamp } from "firebase/firestore";
import firebaseService from "@services/firebase/index";

/** Map a Firestore workshop_jobs doc to a frontend WorkshopJob */
function firestoreDocToJob(id: string, d: Record<string, any>): WorkshopJob {
  return {
    id,
    clientName: d.client_name || "",
    plate: d.plate || "",
    hours: d.hours || 0,
    workerId: d.worker_id || "",
    date: d.date || "",
    startHour: d.start_hour || 0,
    durationHours: d.duration_hours || 0,
    status: d.status || "en_attente",
    specialty: d.specialty || "autre",
    hasCarIcon: d.has_car_icon || false,
    ordre_id: d.ordre_id || undefined,
    org_id: d.org_id || "",
    autoGenerated: d.auto_generated || false,
    bookingId: d.booking_id || undefined,
    vehicleName: d.vehicle_name || undefined,
    interventions: d.interventions || undefined,
  };
}

/** Map a Firestore work_charges doc to a frontend WorkCharge */
function firestoreDocToCharge(id: string, d: Record<string, any>): WorkCharge {
  return {
    id,
    clientName: d.client_name || "",
    hours: d.hours || 0,
    status: d.status || "a_repartir",
    specialty: d.specialty || "autre",
    ordre_id: d.ordre_id || undefined,
    org_id: d.org_id || "",
    autoGenerated: d.auto_generated || false,
    bookingId: d.booking_id || undefined,
  };
}

/** Roles that map to workshop specialties (used as fallback) */
const ROLE_TO_SPECIALTY: Record<string, WorkshopSpecialty> = {
  mecanicien: "mecanique",
  carrossier: "carrosserie",
  peintre: "peinture",
  diagnosticien: "diagnostique",
  chef_atelier: "autre",
};

export const JOB_STATUSES: WorkshopStatusConfig[] = [
  { id: "en_attente", label: "En attente", color: "#2563EB", bgColor: "#DBEAFE", borderColor: "#93C5FD" },
  { id: "en_cours", label: "En cours", color: "#5C6BFF", bgColor: "#CACFFF", borderColor: "#A5ADFF" },
  { id: "fin_travaux", label: "Fin de travaux", color: "#7C3AED", bgColor: "#EDE9FE", borderColor: "#C4B5FD" },
  { id: "disponible", label: "Disponible", color: "#8258FF", bgColor: "#F1E7F9", borderColor: "#D4BFFF" },
  { id: "restitue", label: "Restitué", color: "#14B8A6", bgColor: "#CCFBF1", borderColor: "#99F6E4" },
];

/** Map an ordre status to the corresponding job status for display on the atelier grid */
const ORDRE_TO_JOB_STATUS: Record<OrdreStatus, WorkshopJobStatus> = {
  rdv: "en_attente",
  a_preparer: "en_attente",
  devis_initial: "en_attente",
  commande: "en_attente",
  pret: "en_attente",
  reception: "en_attente",
  en_cours: "en_cours",
  devis_supp: "en_cours",
  accord: "en_cours",
  acompte: "en_cours",
  fin_de_travaux: "fin_travaux",
  facturation: "fin_travaux",
  solde: "fin_travaux",
  solde_paye: "fin_travaux",
  disponible: "disponible",
  restitue: "restitue",
  arrete: "en_attente",
};

export function getJobStatusFromOrdre(ordreStatus: OrdreStatus | undefined): WorkshopJobStatus {
  if (!ordreStatus) return "en_attente";
  return ORDRE_TO_JOB_STATUS[ordreStatus] || "en_attente";
}

// Workers are fetched from API but we keep a local cache
let _cachedWorkers: WorkshopWorker[] = [];
export function getCachedWorkers(): WorkshopWorker[] {
  return _cachedWorkers;
}

let _nextId = 100;
function genId() {
  return `gen_${_nextId++}`;
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Get Mon-Fri date range for the week containing dateStr */
function getWeekRange(dateStr: string): { date_from: string; date_to: string } {
  const d = new Date(dateStr);
  const day = d.getDay(); // 0=Sun
  const diffToMon = day === 0 ? -6 : 1 - day;
  const mon = new Date(d);
  mon.setDate(d.getDate() + diffToMon);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 4);
  return { date_from: toISO(mon), date_to: toISO(fri) };
}

interface AtelierState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  /** Row-height density of the grid (compact fits more of the day on screen). */
  compaction: CompactionMode;
  /** Métiers shown in the grid. Empty array = "Tous" (all métiers). */
  activeSpecialties: string[];
  selectedWeekWorkerId: string;
  selectedWorkerIds: string[];
  jobs: WorkshopJob[];
  charges: WorkCharge[];
  workers: WorkshopWorker[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  hoveredChargeId: string | null;
  highlightOrdreId: string | null;
  highlightJobId: string | null;
  carWindows: Record<string, CarWindow>;
  _fetchedOrgId: string | undefined;
  _loadedRange: { date_from: string; date_to: string } | undefined;

  fetchData: (orgId: string, force?: boolean) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  setCompaction: (mode: CompactionMode) => void;
  /** Toggle a métier in/out of the active filter. */
  toggleSpecialty: (s: string) => void;
  /** Reset the filter to "Tous" (show all métiers). */
  setAllSpecialties: () => void;
  setSelectedWeekWorkerId: (id: string) => void;
  toggleWorkerId: (id: string) => void;
  setSelectedWorkerIds: (ids: string[]) => void;
  setSearchQuery: (query: string) => void;
  setHoveredChargeId: (id: string | null) => void;
  setHighlightOrdreId: (id: string | null) => void;
  setHighlightJobId: (id: string | null) => void;
  updateJob: (id: string, updates: Partial<Pick<WorkshopJob, "startHour" | "durationHours" | "workerId" | "date">>) => void;
  getFilteredJobs: () => WorkshopJob[];
  getVisibleWorkers: () => WorkshopWorker[];
  placeChargeAsJob: (chargeId: string, workerId: string, startHour: number, durationHours: number, date?: string) => void;
  splitCharge: (chargeId: string, keepHours: number) => void;
  mergeCharges: (keepId: string, deleteId: string) => void;
  mergeJobs: (keepId: string, deleteId: string, newStartHour: number, newDurationHours: number) => void;
  returnJobToCharge: (jobId: string) => void;
  moveChargeToStatus: (chargeId: string, newStatus: WorkCharge["status"]) => void;
  confirmJob: (jobId: string) => void;
  invalidateCarWindows: (ordreId: string) => void;
  _realtimeUnsubs: (() => void)[];
  startRealtimeListener: (orgId: string) => void;
  stopRealtimeListener: () => void;
}

export const useAtelierStore = create<AtelierState>()(
  persist(
    (set, get) => ({
  selectedDate: todayIso(),
  viewMode: "jour",
  compaction: "normal" as CompactionMode,
  activeSpecialties: [],
  selectedWeekWorkerId: "",
  selectedWorkerIds: [],
  jobs: [],
  charges: [],
  workers: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: "",
  hoveredChargeId: null,
  highlightOrdreId: null,
  highlightJobId: null,
  carWindows: {},
  _fetchedOrgId: undefined,
  _loadedRange: undefined,
  _realtimeUnsubs: [],

  fetchData: async (orgId: string, force?: boolean) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const range = getWeekRange(state.selectedDate);
    // Skip if we already have this week's data (unless forced)
    if (!force && state._loadedRange && state._loadedRange.date_from === range.date_from && state._loadedRange.date_to === range.date_to && state._fetchedOrgId === orgId) return;
    const showLoading = state.jobs.length === 0 && state.charges.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const [jobs, charges, blocks] = await Promise.all([
        atelierApi.listJobs({ org_id: orgId, date_from: range.date_from, date_to: range.date_to, limit: 1000 }),
        atelierApi.listCharges({ org_id: orgId, limit: 1000 }),
        disponibilitesApi.list({ org_id: orgId }),
      ]);

      // Derive workers from dispo blocks — anyone with a non-conge/pause block is a worker
      const orgMembers = useDataStore.getState().orgMembers;
      const memberMap = new Map(orgMembers.map((m) => [m.id, m]));
      const workerZones: Record<string, string[]> = {};
      for (const b of blocks) {
        const zone = b.zoneType || "mecanique";
        if (zone === "conge" || zone === "pause") continue;
        if (!workerZones[b.workerId]) workerZones[b.workerId] = [];
        if (!workerZones[b.workerId].includes(zone)) workerZones[b.workerId].push(zone);
      }

      const workers: WorkshopWorker[] = [];
      const workerIds = new Set<string>();

      // Add all members with workshop roles (always visible, even without blocks)
      for (const m of orgMembers) {
        if (m.status === "inactive" || workerIds.has(m.id)) continue;
        const roleSpecialty = ROLE_TO_SPECIALTY[m.role];
        if (!roleSpecialty) continue;
        workers.push({
          id: m.id,
          name: m.infos?.display_name || m.infos?.full_name || "?",
          specialty: (m.config as any)?.team || roleSpecialty,
        });
        workerIds.add(m.id);
      }

      // Add anyone with dispo blocks who isn't already in the list
      Object.keys(workerZones).forEach((wid) => {
        if (workerIds.has(wid)) return;
        const member = memberMap.get(wid);
        if (!member || member.status === "inactive") return;
        const zones = workerZones[wid] || [];
        const specialty: WorkshopSpecialty =
          (member.config as any)?.team ||
          ROLE_TO_SPECIALTY[member.role] ||
          (zones.includes("mecanique") ? "mecanique" :
           zones.includes("carrosserie") ? "carrosserie" :
           zones.includes("service_rapide") ? "mecanique" : "mecanique");
        workers.push({
          id: member.id,
          name: member.infos?.display_name || member.infos?.full_name || "?",
          specialty,
        });
        workerIds.add(member.id);
      });

      // Ensure any worker referenced by a job is in the list
      for (const j of jobs) {
        if (j.workerId && !workerIds.has(j.workerId)) {
          const member = memberMap.get(j.workerId);
          if (member) {
            workers.push({
              id: member.id,
              name: member.infos?.display_name || member.infos?.full_name || "?",
              specialty: (member.config as any)?.team || "autre",
            });
            workerIds.add(member.id);
          }
        }
      }

      _cachedWorkers = workers;
      // Build carWindows from current jobs and charges only (bounded by fetched data)
      // Start fresh each fetch to avoid unbounded growth from stale entries
      const newCarWindows: Record<string, CarWindow> = {};
      for (const j of jobs) {
        if (j.ordre_id && j.carWindow) {
          newCarWindows[j.ordre_id] = j.carWindow;
        }
      }
      for (const c of charges) {
        if (c.ordre_id && c.carWindow) {
          newCarWindows[c.ordre_id] = c.carWindow;
        }
      }
      // Enrich jobs/charges that are missing carWindow from the persistent store
      const enrichedJobs = jobs.map((j) =>
        !j.carWindow && j.ordre_id && newCarWindows[j.ordre_id]
          ? { ...j, carWindow: newCarWindows[j.ordre_id] } : j,
      );
      const enrichedCharges = charges.map((c) =>
        !c.carWindow && c.ordre_id && newCarWindows[c.ordre_id]
          ? { ...c, carWindow: newCarWindows[c.ordre_id] } : c,
      );
      const currentWeekWorker = get().selectedWeekWorkerId;
      const validWeekWorker = currentWeekWorker === "tous" || workers.some((w) => w.id === currentWeekWorker);
      set({
        jobs: enrichedJobs, charges: enrichedCharges, workers, carWindows: newCarWindows, isLoading: false, isRefreshing: false, _loadedRange: range,
        ...((!currentWeekWorker || !validWeekWorker) && workers.length > 0 ? { selectedWeekWorkerId: workers[0].id } : {}),
      });
    } catch (error) {
      console.error("Error fetching atelier data:", error);
      set({ jobs: [], charges: [], workers: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined, _loadedRange: undefined });
    }
  },

  setSelectedDate: (date) => {
    const state = get();
    set({ selectedDate: date });
    // Keep the reception planning on the same day — the selected date is shared
    // across both plannings. Mirror the value (and force its next fetch) without
    // calling its setter, so there's no ping-pong. It re-fetches when opened.
    if (usePlanningStore.getState().selectedDate !== date) {
      usePlanningStore.setState({ selectedDate: date, _loadedRange: undefined });
    }
    // Re-fetch if the new date falls outside the loaded week
    const newRange = getWeekRange(date);
    if (state._fetchedOrgId && (!state._loadedRange || state._loadedRange.date_from !== newRange.date_from)) {
      set({ _loadedRange: undefined, isRefreshing: false });
      get().fetchData(state._fetchedOrgId);
    }
  },
  setViewMode: (mode) => set({ viewMode: mode }),
  setCompaction: (mode) => set({ compaction: mode }),
  toggleSpecialty: (s) => set((state) => {
    const cur = state.activeSpecialties ?? [];
    return { activeSpecialties: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
  }),
  setAllSpecialties: () => set({ activeSpecialties: [] }),
  setSelectedWeekWorkerId: (id) => set({ selectedWeekWorkerId: id }),
  toggleWorkerId: (id) => set((state) => {
    const ids = state.selectedWorkerIds;
    const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
    // If empty after untoggling last one, reset to all workers
    return { selectedWorkerIds: next.length === 0 ? state.workers.map((w) => w.id) : next };
  }),
  setSelectedWorkerIds: (ids) => set({ selectedWorkerIds: ids }),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setHoveredChargeId: (id) => set({ hoveredChargeId: id }),
  setHighlightOrdreId: (id) => {
    set({ highlightOrdreId: id });
    if (id) setTimeout(() => set((s) => s.highlightOrdreId === id ? { highlightOrdreId: null } : {}), 5000);
  },
  setHighlightJobId: (id) => {
    set({ highlightJobId: id });
    if (id) setTimeout(() => set((s) => s.highlightJobId === id ? { highlightJobId: null } : {}), 5000);
  },

  updateJob: (id, updates) => {
    const positionChanged = updates.startHour !== undefined || updates.durationHours !== undefined || updates.workerId !== undefined;
    const wasAuto = positionChanged && get().jobs.find((j) => j.id === id)?.autoGenerated;
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...updates, ...(wasAuto ? { autoGenerated: false } : {}) } : j)),
    }));
    atelierApi.updateJob(id, updates as Partial<WorkshopJob>).catch((e) => console.error("Error updating job:", e));
    if (wasAuto) {
      atelierApi.confirmJob(id).catch((e) => console.error("Error confirming job:", e));
    }
  },

  placeChargeAsJob: (chargeId, workerId, startHour, durationHours, date) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      const worker = state.workers.find((w) => w.id === workerId);
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const jobDate = date ?? state.selectedDate;
      const tempId = genId();
      const newJob: WorkshopJob = {
        id: tempId,
        clientName: charge.clientName,
        plate: "",
        hours: durationHours,
        workerId,
        date: jobDate,
        startHour,
        durationHours,
        status: "en_cours",
        specialty: worker?.specialty ?? charge.specialty,
        ordre_id: charge.ordre_id,
        org_id: orgId,
        carWindow: charge.carWindow || (charge.ordre_id ? state.carWindows[charge.ordre_id] : undefined),
      };
      const remainingHours = +(charge.hours - durationHours).toFixed(2);
      const newCharges = remainingHours > 0
        ? state.charges.map((c) => (c.id === chargeId ? { ...c, hours: remainingHours } : c))
        : state.charges.filter((c) => c.id !== chargeId);

      // Persist to API
      atelierApi.createJob({ ...newJob, org_id: orgId } as Omit<WorkshopJob, "id"> & { org_id: string }).then((created) => {
        // Replace temp id with real id, preserving carWindow since backend doesn't store it
        useAtelierStore.setState((s) => ({
          jobs: s.jobs.map((j) => (j.id === tempId ? { ...created, carWindow: j.carWindow ?? created.carWindow } : j)),
        }));
      }).catch((e) => console.error("Error creating job:", e));

      if (remainingHours > 0) {
        atelierApi.updateCharge(chargeId, { hours: remainingHours }).catch((e) => console.error("Error updating charge:", e));
      } else {
        atelierApi.deleteCharge(chargeId).catch((e) => console.error("Error deleting charge:", e));
      }

      // Persist carWindow before charge is potentially deleted
      const resolvedCarWindow = charge.carWindow || (charge.ordre_id ? state.carWindows[charge.ordre_id] : undefined);
      const updatedCarWindows = charge.ordre_id && resolvedCarWindow
        ? { ...state.carWindows, [charge.ordre_id]: resolvedCarWindow }
        : state.carWindows;
      return { jobs: [...state.jobs, newJob], charges: newCharges, carWindows: updatedCarWindows };
    }),

  splitCharge: (chargeId, keepHours) =>
    set((state) => {
      const charge = state.charges.find((c) => c.id === chargeId);
      if (!charge) return state;
      const remainder = +(charge.hours - keepHours).toFixed(2);
      if (remainder <= 0 || keepHours <= 0) return state;
      const orgId = charge.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: charge.clientName,
        hours: remainder,
        status: charge.status,
        specialty: charge.specialty,
        ordre_id: charge.ordre_id,
        org_id: orgId,
        carWindow: charge.carWindow,
      };

      // Persist: update original charge hours, create new charge for remainder
      atelierApi.updateCharge(chargeId, { hours: keepHours }).catch((e) => console.error("Error updating charge:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating split charge:", e));

      return {
        charges: [
          ...state.charges.map((c) => (c.id === chargeId ? { ...c, hours: keepHours } : c)),
          newCharge,
        ],
      };
    }),

  mergeCharges: (keepId, deleteId) =>
    set((state) => {
      const keep = state.charges.find((c) => c.id === keepId);
      const del = state.charges.find((c) => c.id === deleteId);
      if (!keep || !del) return state;
      const mergedHours = +(keep.hours + del.hours).toFixed(2);
      atelierApi.updateCharge(keepId, { hours: mergedHours }).catch((e) => console.error("Error merging charge:", e));
      atelierApi.deleteCharge(deleteId).catch((e) => console.error("Error deleting merged charge:", e));
      return {
        charges: state.charges
          .filter((c) => c.id !== deleteId)
          .map((c) => (c.id === keepId ? { ...c, hours: mergedHours } : c)),
      };
    }),

  mergeJobs: (keepId, deleteId, newStartHour, newDurationHours) =>
    set((state) => {
      const keep = state.jobs.find((j) => j.id === keepId);
      const del = state.jobs.find((j) => j.id === deleteId);
      if (!keep || !del) return state;
      atelierApi.updateJob(keepId, { startHour: newStartHour, durationHours: newDurationHours }).catch((e) => console.error("Error merging job:", e));
      atelierApi.deleteJob(deleteId).catch((e) => console.error("Error deleting merged job:", e));
      return {
        jobs: state.jobs
          .filter((j) => j.id !== deleteId)
          .map((j) => (j.id === keepId ? { ...j, startHour: newStartHour, durationHours: newDurationHours } : j)),
      };
    }),

  returnJobToCharge: (jobId) =>
    set((state) => {
      const job = state.jobs.find((j) => j.id === jobId);
      if (!job) return state;
      const orgId = job.org_id || useDataStore.getState().orgData?.id || "";
      const tempId = genId();
      // Carry carWindow from job itself, sibling charge, or persistent store
      const siblingCarWindow = job.carWindow
        || (job.ordre_id
          ? (state.charges.find((c) => c.ordre_id === job.ordre_id && c.carWindow)?.carWindow
            || state.carWindows[job.ordre_id])
          : undefined);
      const newCharge: WorkCharge = {
        id: tempId,
        clientName: job.clientName,
        hours: job.durationHours,
        status: "a_repartir",
        specialty: job.specialty,
        ordre_id: job.ordre_id,
        org_id: orgId,
        carWindow: siblingCarWindow,
      };
      atelierApi.deleteJob(jobId).catch((e) => console.error("Error deleting job:", e));
      atelierApi.createCharge({ ...newCharge, org_id: orgId } as Omit<WorkCharge, "id"> & { org_id: string }).then((created) => {
        useAtelierStore.setState((s) => ({
          charges: s.charges.map((c) => (c.id === tempId ? { ...created, carWindow: c.carWindow ?? created.carWindow } : c)),
        }));
      }).catch((e) => console.error("Error creating charge:", e));
      return {
        jobs: state.jobs.filter((j) => j.id !== jobId),
        charges: [...state.charges, newCharge],
      };
    }),

  moveChargeToStatus: (chargeId, newStatus) => {
    set((state) => ({
      charges: state.charges.map((c) => (c.id === chargeId ? { ...c, status: newStatus } : c)),
    }));
    atelierApi.updateCharge(chargeId, { status: newStatus }).catch((e) => console.error("Error updating charge:", e));
  },

  confirmJob: (jobId) => {
    set((state) => ({
      jobs: state.jobs.map((j) => (j.id === jobId ? { ...j, autoGenerated: false } : j)),
    }));
    atelierApi.confirmJob(jobId).catch((e) => console.error("Error confirming job:", e));
  },

  invalidateCarWindows: (ordreId) => {
    // Remove cached car window so it gets re-fetched from appointments on next fetchData
    set((state) => {
      const { [ordreId]: _, ...rest } = state.carWindows;
      return {
        carWindows: rest,
        // Also clear carWindow from jobs/charges for this ordre so they re-resolve
        jobs: state.jobs.map((j) => j.ordre_id === ordreId ? { ...j, carWindow: undefined } : j),
        charges: state.charges.map((c) => c.ordre_id === ordreId ? { ...c, carWindow: undefined } : c),
      };
    });
    // Re-fetch from API to get updated car windows
    const orgId = get()._fetchedOrgId;
    if (orgId) {
      set({ isRefreshing: false });
      get().fetchData(orgId, true);
    }
  },

  getFilteredJobs: () => {
    const state = get();
    const metiers = state.activeSpecialties ?? [];
    return state.jobs.filter(
      (j) =>
        j.date === state.selectedDate &&
        (metiers.length === 0 || metiers.includes(j.specialty)) &&
        (state.searchQuery === "" ||
          j.clientName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
          j.plate.toLowerCase().includes(state.searchQuery.toLowerCase()))
    );
  },
  // Worker columns follow the active métier filter: selecting "Carrosserie" shows
  // only carrossiers, "Mécanique" only mécaniciens, etc. (empty filter = all).
  getVisibleWorkers: () => {
    const state = get();
    const metiers = state.activeSpecialties ?? [];
    if (metiers.length === 0) return state.workers;
    return state.workers.filter((w) => metiers.includes(w.specialty));
  },

  startRealtimeListener: (orgId: string) => {
    // Stop any existing listeners first
    get().stopRealtimeListener();
    const listenSince = Timestamp.now();
    const db = firebaseService.db;

    // Listen for job changes
    const jobsQuery = query(
      collection(db, "workshop_jobs"),
      where("org_id", "==", orgId),
      where("updated_at", ">=", listenSince),
    );
    const unsubJobs = onSnapshot(jobsQuery, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      console.log(`[atelier-realtime] ${snapshot.docChanges().length} job(s) updated`);
      set((state) => {
        let jobs = [...state.jobs];
        const carWindows = { ...state.carWindows };
        for (const change of snapshot.docChanges()) {
          const doc = change.doc;
          const id = doc.id;
          if (change.type === "removed") {
            jobs = jobs.filter((j) => j.id !== id);
          } else {
            const newJob = firestoreDocToJob(id, doc.data());
            // Enrich with existing carWindow if available
            if (!newJob.carWindow && newJob.ordre_id && carWindows[newJob.ordre_id]) {
              newJob.carWindow = carWindows[newJob.ordre_id];
            }
            const idx = jobs.findIndex((j) => j.id === id);
            if (idx >= 0) {
              jobs[idx] = newJob;
            } else {
              // Race: realtime may fire before createJob response replaces the
              // temp gen_* id. Match by stable fields and swap in place.
              const tempIdx = jobs.findIndex((j) =>
                j.id.startsWith("gen_") &&
                j.ordre_id === newJob.ordre_id &&
                j.workerId === newJob.workerId &&
                j.date === newJob.date &&
                j.startHour === newJob.startHour,
              );
              if (tempIdx >= 0) {
                jobs[tempIdx] = newJob;
              } else {
                jobs.push(newJob);
              }
            }
          }
        }
        return { jobs };
      });
    }, (err) => {
      console.error("[atelier-realtime] jobs listener error:", err);
    });

    // Listen for charge changes
    const chargesQuery = query(
      collection(db, "work_charges"),
      where("org_id", "==", orgId),
      where("updated_at", ">=", listenSince),
    );
    const unsubCharges = onSnapshot(chargesQuery, (snapshot) => {
      if (snapshot.docChanges().length === 0) return;
      console.log(`[atelier-realtime] ${snapshot.docChanges().length} charge(s) updated`);
      set((state) => {
        let charges = [...state.charges];
        for (const change of snapshot.docChanges()) {
          const doc = change.doc;
          const id = doc.id;
          if (change.type === "removed") {
            charges = charges.filter((c) => c.id !== id);
          } else {
            const newCharge = firestoreDocToCharge(id, doc.data());
            // Enrich with existing carWindow if available
            if (!newCharge.carWindow && newCharge.ordre_id) {
              const cw = state.carWindows[newCharge.ordre_id];
              if (cw) newCharge.carWindow = cw;
            }
            const idx = charges.findIndex((c) => c.id === id);
            if (idx >= 0) {
              charges[idx] = newCharge;
            } else {
              // Race: realtime may fire before createCharge response replaces
              // the temp gen_* id. Match by stable fields and swap in place.
              const tempIdx = charges.findIndex((c) =>
                c.id.startsWith("gen_") &&
                c.ordre_id === newCharge.ordre_id &&
                c.specialty === newCharge.specialty &&
                c.status === newCharge.status &&
                Math.abs(c.hours - newCharge.hours) < 0.01,
              );
              if (tempIdx >= 0) {
                charges[tempIdx] = newCharge;
              } else {
                charges.push(newCharge);
              }
            }
          }
        }
        return { charges };
      });
    }, (err) => {
      console.error("[atelier-realtime] charges listener error:", err);
    });

    set({ _realtimeUnsubs: [unsubJobs, unsubCharges] });
  },

  stopRealtimeListener: () => {
    const unsubs = get()._realtimeUnsubs;
    for (const unsub of unsubs) unsub();
    set({ _realtimeUnsubs: [] });
  },
    }),
    {
      name: "atelier-prefs",
      version: 1,
      // v0 → v1: single `activeSpecialty` ("all" | métier) became `activeSpecialties` (string[], [] = Tous)
      migrate: (persisted: any, version: number) => {
        if (version < 1 && persisted && "activeSpecialty" in persisted) {
          const old = persisted.activeSpecialty;
          persisted.activeSpecialties = !old || old === "all" ? [] : [old];
          delete persisted.activeSpecialty;
        }
        return persisted;
      },
      partialize: (state) => ({
        viewMode: state.viewMode,
        compaction: state.compaction,
        selectedDate: state.selectedDate,
        activeSpecialties: state.activeSpecialties,
        selectedWeekWorkerId: state.selectedWeekWorkerId,
        selectedWorkerIds: state.selectedWorkerIds,
      }),
    },
  ),
);
